import { useForm } from 'react-hook-form';
import { useQueryClient } from '@tanstack/react-query';

import { payrunQueryKeys } from '@xemplo/gp-constants';
import { useUpdatePayrunAssignee } from '@xemplo/payrun-query';
import { safeCallAsync } from '@xemplo/safe-call';
import { ToastType, useToast } from '@xemplo/toast';

import {
  AssignPayrollManagerContentProps,
  AssignPayrollManagerFormValues,
} from './assign-payroll-manager.types';

const ASSIGN_SUCCESS_TOAST = 'Payroll manager assigned';

export const usePayrunAssigneeModal = (props: AssignPayrollManagerContentProps) => {
  const { payrun, modalDialog, onSuccess } = props;
  const payrunId = payrun?.id;

  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const { mutateAsync: updateAssignee } = useUpdatePayrunAssignee({});

  const methods = useForm<AssignPayrollManagerFormValues>({
    defaultValues: { assigneeUserId: payrun.assigneeUserInfo?.userId },
  });

  function handleClose() {
    methods.reset();
    modalDialog.closeDialog();
  }

  const handleSubmit = methods.handleSubmit(async (formData) => {
    if (!payrunId) return;

    // Errors surface via the consumer's global handler — no local error toast.
    const [, error] = await safeCallAsync(
      updateAssignee({
        assigneeUserId: formData.assigneeUserId,
        notes: formData.notes,
        payrunId,
      }),
    );
    if (error) return;

    // Awaited so the payrun the caller passes us is refreshed before the modal can be
    // reopened — otherwise an immediate reopen prefills the previous manager.
    await queryClient.invalidateQueries({ queryKey: payrunQueryKeys.all });
    addToast({ text: ASSIGN_SUCCESS_TOAST, type: ToastType.Confirmation });
    handleClose();
    onSuccess?.();
  });

  return {
    handleSubmit,
    handleClose,
    methods,
  };
};
